import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  CircularProgress,
  useTheme,
  Fade,
  Avatar,
  Divider,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';

interface Message {
  id: number;
  text: string;
  isBot: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activeRequestName: string;
  onSendPrompt: (prompt: string) => Promise<string>;
}

export const ScheduleAiAssistant: React.FC<Props> = ({
  isOpen,
  onClose,
  activeRequestName,
  onSendPrompt,
}) => {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  // Initial greeting when opened for a specific request
  useEffect(() => {
    if (isOpen && messages.length === 0 && activeRequestName) {
      setMessages([
        {
          id: Date.now(),
          text: `¡Hola! Soy tu Asistente Inteligente ✨.\n\nHe generado la propuesta base para "${activeRequestName.split(' | ')[0]}". Si deseas realizar algún cambio específico (ej. "Descanso para Carlos el lunes", "Cambia el turno de Ana"), dímelo y ajustaré el modelo.`,
          isBot: true,
        },
      ]);
    }
  }, [isOpen, activeRequestName, messages.length]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { id: Date.now(), text: input, isBot: false };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const botResponse = await onSendPrompt(userMessage.text);

      setMessages((prev) => [...prev, { id: Date.now(), text: botResponse, isBot: true }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          text: 'Ocurrió un error al procesar tu solicitud. Intenta nuevamente.',
          isBot: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(16px)',
          borderLeft: '1px solid rgba(102, 60, 193, 0.2)',
          boxShadow: '-10px 0 30px rgba(102, 60, 193, 0.1)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header Premium */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #663cc1 0%, #9c27b0 100%)',
          color: 'white',
          p: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
              <AutoAwesomeIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                Asistente IA
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#4caf50' }} /> En
                línea
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            p: 0.8,
            bgcolor: 'rgba(0,0,0,0.15)',
            borderRadius: 2,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Editando: <b>{activeRequestName ? activeRequestName.split(' | ')[0] : ''}</b>
        </Typography>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.map((msg, index) => (
          <Fade in={true} key={msg.id} timeout={300 + (index * 100 > 500 ? 0 : index * 100)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: msg.isBot ? 'row' : 'row-reverse',
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: msg.isBot ? '#f3f0fc' : '#e65100',
                  color: msg.isBot ? '#663cc1' : 'white',
                  border: msg.isBot ? '1px solid #dcd1f5' : 'none',
                }}
              >
                {msg.isBot ? (
                  <SmartToyIcon sx={{ fontSize: 16 }} />
                ) : (
                  <PersonIcon sx={{ fontSize: 16 }} />
                )}
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  maxWidth: '75%',
                  bgcolor: msg.isBot ? 'white' : '#e65100',
                  color: msg.isBot ? '#333' : 'white',
                  borderRadius: msg.isBot ? '12px 12px 12px 2px' : '12px 12px 2px 12px',
                  border: msg.isBot ? '1px solid #eaeaea' : 'none',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {msg.text}
                </Typography>
              </Paper>
            </Box>
          </Fade>
        ))}

        {isLoading && (
          <Fade in={true}>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 1 }}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: '#f3f0fc',
                  color: '#663cc1',
                  border: '1px solid #dcd1f5',
                }}
              >
                <SmartToyIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor: 'white',
                  borderRadius: '12px 12px 12px 2px',
                  border: '1px solid #eaeaea',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} sx={{ color: '#663cc1' }} />
                <Typography variant="caption" sx={{ color: '#663cc1', fontWeight: 'bold' }}>
                  Analizando...
                </Typography>
              </Paper>
            </Box>
          </Fade>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Input */}
      <Box sx={{ p: 2, background: 'rgba(255,255,255,0.9)' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'white',
            p: 0.5,
            borderRadius: 8,
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
          }}
        >
          <TextField
            fullWidth
            variant="standard"
            placeholder="Ej: Dar descanso a Ana el domingo..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSend();
            }}
            disabled={isLoading}
            InputProps={{
              disableUnderline: true,
              sx: { px: 2, py: 0.5, fontSize: '0.9rem' },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            sx={{
              bgcolor: input.trim() ? '#663cc1' : '#f5f5f5',
              color: input.trim() ? 'white' : '#aaa',
              '&:hover': { bgcolor: input.trim() ? '#522b9c' : '#f5f5f5' },
              transition: 'all 0.2s',
              mr: 0.5,
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#999' }}
        >
          La IA re-generará el horario respetando tus ajustes.
        </Typography>
      </Box>
    </Drawer>
  );
};
